import { Container, Sprite } from 'pixi.js';
import { AssetLoader } from './AssetLoader';
import { Treat } from './Treat';

export type PlatformSize = 'short' | 'medium' | 'long';

export class Platform extends Container {
  private sprite: Sprite;
  private treats: Treat[] = [];
  private platformSize: PlatformSize;
  private platformWidth: number;
  private platformHeight: number;
  private spriteScale = 0.2;

  constructor(x: number, y: number, size: PlatformSize) {
    super();

    this.platformSize = size;

    const assetLoader = AssetLoader.getInstance();
    const texture = assetLoader.getTexture(`platform_${size}`);

    this.sprite = new Sprite(texture);
    this.sprite.anchor.set(0.5, 1);
    this.sprite.scale.set(this.spriteScale);

    this.platformWidth = this.sprite.width;
    this.platformHeight = this.sprite.height;

    this.x = x;
    this.y = y;

    this.addChild(this.sprite);

    this.spawnTreats();
  }

  private spawnTreats(): void {
    const treatCount = this.getTreatCount();
    const spacing = this.platformWidth / (treatCount + 1);

    for (let i = 0; i < treatCount; i++) {
      // Position relative to platform (0, 0 is platform's position)
      const treatX = -this.platformWidth / 2 + spacing * (i + 1);
      const treatY = -this.platformHeight - 30; // Position above platform

      const treat = new Treat(treatX, treatY);
      this.treats.push(treat);
      this.addChild(treat);
    }
  }

  private getTreatCount(): number {
    switch (this.platformSize) {
      case 'short':
        return 1;
      case 'medium':
        return 3;
      case 'long':
        return 5;
      default:
        return 1;
    }
  }

  update(scrollSpeed: number, deltaTime: number): void {
    this.x -= scrollSpeed * deltaTime;

    // Update treat animations (they move with the platform automatically as children)
    for (const treat of this.treats) {
      treat.update(deltaTime);
    }

    // Remove completed treat animations
    this.treats = this.treats.filter(treat => {
      if (treat.isAnimationComplete()) {
        this.removeChild(treat);
        return false;
      }
      return true;
    });
  }

  isOffScreen(): boolean {
    return this.x + this.platformWidth / 2 < 0;
  }

  getTopHitbox(): { x: number; y: number; width: number; height: number } {
    const hitboxHeight = 20; // Thin collision area on top

    return {
      x: this.x - this.platformWidth / 2,
      y: this.y - this.platformHeight - hitboxHeight,
      width: this.platformWidth,
      height: hitboxHeight,
    };
  }

  getBottomHitbox(): { x: number; y: number; width: number; height: number } {
    const hitboxHeight = 20; // Thin collision area on bottom

    return {
      x: this.x - this.platformWidth / 2,
      y: this.y - hitboxHeight,
      width: this.platformWidth,
      height: hitboxHeight,
    };
  }

  getFullHitbox(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.x - this.platformWidth / 2,
      y: this.y - this.platformHeight,
      width: this.platformWidth,
      height: this.platformHeight,
    };
  }

  isPlayerOnPlatform(playerX: number): boolean {
    const leftEdge = this.x - this.platformWidth / 2;
    const rightEdge = this.x + this.platformWidth / 2;
    return playerX >= leftEdge && playerX <= rightEdge;
  }

  getPlatformY(): number {
    return this.y - this.platformHeight;
  }

  checkTreatCollision(
    playerHitbox: { x: number; y: number; width: number; height: number },
    playerX: number,
    playerY: number
  ): Treat | null {
    for (const treat of this.treats) {
      if (treat.getIsCollected()) {
        continue;
      }

      const treatHitbox = treat.getHitbox();

      // Convert treat's local coordinates to world coordinates
      const worldTreatX = this.x + treatHitbox.x;
      const worldTreatY = this.y + treatHitbox.y;

      if (
        playerHitbox.x < worldTreatX + treatHitbox.width &&
        playerHitbox.x + playerHitbox.width > worldTreatX &&
        playerHitbox.y < worldTreatY + treatHitbox.height &&
        playerHitbox.y + playerHitbox.height > worldTreatY
      ) {
        // Convert player world position to platform local coordinates
        const localPlayerX = playerX - this.x;
        const localPlayerY = playerY - this.y;
        treat.collect(localPlayerX, localPlayerY);
        return treat;
      }
    }

    return null;
  }

  getLeftEdge(): number {
    return this.x - this.platformWidth / 2;
  }

  getRightEdge(): number {
    return this.x + this.platformWidth / 2;
  }
}
